import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import adminAxios from '../../services/adminAxios';
import { formatDate } from '../../utils/dateHelper';
import '../../assets/css/admin/checkins.css';

const CheckInsPage = () => {
  const [checkins, setCheckins] = useState([]);
  const [filteredCheckins, setFilteredCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [events, setEvents] = useState([]);
  const checkinsPerPage = 15;

  const fetchCheckIns = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      // Fetch all check-ins without pagination first to get complete data
      const response = await adminAxios.get(`/api/admin/checkins?include_details=true`);
      
      if (response.data.success) {
        setCheckins(response.data.checkins);
        
        // Extract unique events for filter dropdown
        const uniqueEvents = [...new Map(
          response.data.checkins
            .filter(checkin => checkin.event_title)
            .map(checkin => [checkin.event_id, { id: checkin.event_id, title: checkin.event_title }])
        ).values()];
        setEvents(uniqueEvents);
        
        setError(null);
      } else {
        setError('Failed to load check-ins');
      }
    } catch (error) {
      console.error('Error fetching check-ins:', error);
      setError('Failed to load check-ins. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and search check-ins
  useEffect(() => {
    let filtered = [...checkins];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(checkin => 
        checkin.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checkin.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        checkin.event_title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply event filter
    if (filterEvent) {
      filtered = filtered.filter(checkin => 
        checkin.event_id?.toString() === filterEvent.toString()
      );
    }

    setFilteredCheckins(filtered);
    
    // Calculate pagination for filtered results
    const pages = Math.ceil(filtered.length / checkinsPerPage);
    setTotalPages(pages);
    
    // Reset to first page when filters change
    if (currentPage > pages && pages > 0) {
      setCurrentPage(1);
    }
  }, [checkins, searchTerm, filterEvent, currentPage]);

  // Get current page check-ins
  const getCurrentPageCheckins = () => {
    const startIndex = (currentPage - 1) * checkinsPerPage;
    const endIndex = startIndex + checkinsPerPage;
    return filteredCheckins.slice(startIndex, endIndex);
  };

  useEffect(() => {
    fetchCheckIns();
  }, [fetchCheckIns]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEventFilterChange = (e) => {
    setFilterEvent(e.target.value);
    setCurrentPage(1);
  };

  const currentPageCheckins = getCurrentPageCheckins();

  return (
    <AdminLayout>
      <div className="container-fluid px-4">
        <h1 className="mt-4">Check-ins Management</h1>
        <ol className="breadcrumb mb-4">
          <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
          <li className="breadcrumb-item active">Check-ins</li>
        </ol>
        
        <AdminCard 
          title={`All Check-ins (${filteredCheckins.length} ${filterEvent ? 'for selected event' : ''} ${searchTerm ? 'matching' : 'total'})`}
          headerActions={
            <div className="d-flex gap-2">
              <input 
                type="text"
                className="form-control form-control-sm"
                placeholder="Search check-ins..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ minWidth: '200px' }}
              />
              <select 
                className="form-select form-select-sm"
                value={filterEvent}
                onChange={handleEventFilterChange}
                style={{ minWidth: '180px' }}
              >
                <option value="">All Events</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>
          }
          noPadding
        >
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover table-bordered checkins-table" style={{backgroundColor: 'var(--dark-bg-secondary)'}}>
                  <thead>
                    <tr style={{backgroundColor: 'var(--dark-bg-tertiary)'}}>
                      <th>User</th>
                      <th>Email</th>
                      <th>Event</th>
                      <th>Check-in Date</th>
                      <th>Visibility</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageCheckins.length > 0 ? currentPageCheckins.map(checkin => (
                      <tr key={checkin.id}>
                        <td className="user-name">{checkin.user_name || 'Unknown User'}</td>
                        <td>{checkin.user_email || '—'}</td>
                        <td className="event-title">{checkin.event_title}</td>
                        <td>{formatDate(checkin.checkin_date)}</td>
                        <td>
                          <span className={`visibility-badge ${checkin.visibility || 'public'}`}>
                            <i className={`fa fa-${checkin.visibility === 'private' ? 'lock' : checkin.visibility === 'friends' ? 'users' : 'globe'} me-1`}></i>
                            {(checkin.visibility || 'Public').charAt(0).toUpperCase() + (checkin.visibility || 'public').slice(1)}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Link to={`/admin/users/${checkin.user_id}`} className="btn btn-sm btn-outline-info">
                              <i className="fa fa-user me-1"></i> User
                            </Link>
                            <Link to={`/admin/events/${checkin.event_id}`} className="btn btn-sm btn-outline-primary">
                              <i className="fa fa-calendar me-1"></i> Event
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="empty-state-inline">
                            <i className="fa fa-check-square-o me-2"></i>
                            <span>
                              {searchTerm || filterEvent 
                                ? 'No check-ins match your search criteria' 
                                : 'No check-ins found'
                              }
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4 px-3 pb-3">
                  <div className="text-muted small">
                    Showing {((currentPage - 1) * checkinsPerPage) + 1} to {Math.min(currentPage * checkinsPerPage, filteredCheckins.length)} of {filteredCheckins.length} check-ins
                  </div>
                  <nav>
                    <ul className="pagination">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <i className="fa fa-chevron-left"></i>
                        </button>
                      </li>
                      
                      {[...Array(totalPages)].map((_, i) => (
                        <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </button>
                        </li>
                      ))}
                      
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          <i className="fa fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </AdminCard>
      </div>
    </AdminLayout>
  );
};

export default CheckInsPage;
